import React from "react";
import { MoreHorizontal, Eye, Pencil, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableRowActionsWrapper } from "./TableRowActionsWrapper";
import type { BaseEntity, SimpleTableConfig } from "../../types";

// Unified component for row actions that always calls hooks consistently
export interface RowActionsProps<TData extends BaseEntity> {
  item: TData;
  config: SimpleTableConfig<TData>;
  hooks?: import("../../types/shared").EntityHooks<TData>;
  onEdit?: (item: TData) => void;
  onView?: (item: TData) => void;
  onDelete?: (item: TData) => void;
  compact?: boolean;
}

function RowActionsComponent<TData extends BaseEntity>({
  item,
  config,
  hooks,
  onEdit,
  onView,
  onDelete,
  compact = true,
}: RowActionsProps<TData>) {
  // Always render the same component structure - handle conditional logic inside
  if (hooks && config.detailActions && config.detailActions.length > 0) {
    return (
      <TableRowActionsWrapper
        item={item}
        actions={config.detailActions}
        hooks={hooks}
        entityName={config.entity}
        config={config}
        compact={compact}
        maxVisibleActions={config.rowActionsMaxPrimaryIcons ?? 2}
      />
    );
  }

  // Fallback to old hardcoded actions
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center"
          title="More actions"
          aria-label="More actions"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {config.actions?.view && onView && (
          <DropdownMenuItem onClick={() => onView(item)}>
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </DropdownMenuItem>
        )}
        {config.actions?.edit && onEdit && (
          <DropdownMenuItem onClick={() => onEdit(item)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit Task
          </DropdownMenuItem>
        )}
        {config.actions?.delete && onDelete && (
          <DropdownMenuItem
            onClick={() => onDelete(item)}
            className="text-red-600 focus:text-red-600"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const RowActions = React.memo(RowActionsComponent) as typeof RowActionsComponent;
